import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FemaleIcon from '@mui/icons-material/Female';
import HomeIcon from '@mui/icons-material/Home';
import MaleIcon from '@mui/icons-material/Male';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { HorseIcon } from '../../icons/sidebarIcons';
import api from '../../services/api';
import {
  SMART_COLORS as COLORS,
  smartCardSx as cardSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';

function genderMeta(gender, t) {
  if (gender === 'S') return { label: t('stallion') || 'Stallion', icon: <MaleIcon fontSize="small" />, color: COLORS.blue, bg: COLORS.blueSoft };
  if (gender === 'M') return { label: t('mare') || 'Mare', icon: <FemaleIcon fontSize="small" />, color: COLORS.rose, bg: COLORS.roseSoft };
  return { label: gender || '-', icon: null, color: COLORS.muted, bg: COLORS.cardSoft };
}

export default function OwnerHorsesList() {
  const { t, i18n } = useTranslation();
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const [horses, setHorses] = useState([]);
  const [ownerName, setOwnerName] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [horsesRes, ownerRes] = await Promise.all([
          api.get(`/owners/${ownerId}/horses-with-details`),
          api.get(`/owners/${ownerId}`).catch(() => ({ data: null })),
        ]);
        setHorses(Array.isArray(horsesRes.data) ? horsesRes.data : []);
        const owner = ownerRes.data || {};
        setOwnerName(`${owner.first_name || ''} ${owner.last_name || ''}`.trim());
      } catch (err) {
        setError(t('error_fetching_horses') || 'Failed to fetch horses');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ownerId, t]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return horses;
    return horses.filter((horse) => [
      horse.name,
      horse.name_arabic,
      horse.rfid_chip_number,
      horse.breed_arabic,
      horse.breed_english,
      horse.color_arabic,
      horse.color_english,
      horse.stable_name,
      horse.room_name,
    ].filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [horses, search]);

  const stats = useMemo(() => ({
    total: horses.length,
    stallions: horses.filter((horse) => horse.gender === 'S').length,
    mares: horses.filter((horse) => horse.gender === 'M').length,
    housed: horses.filter((horse) => horse.stable_name && horse.room_name).length,
  }), [horses]);

  const isArabic = String(i18n.language || '').startsWith('ar');

  return (
    <Box
      className="smart-page-container"
      dir={isArabic ? 'rtl' : 'ltr'}
      sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}
    >
      <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ width: 64, height: 64, bgcolor: COLORS.goldSoft, color: COLORS.gold }}>
              <HorseIcon size={30} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('owner_horses_list') || 'Owner horses'}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {ownerName || `${t('owner') || 'Owner'} #${ownerId}`} · {t('owner_horses_hint') || 'A compact horse list connected to the owner profile.'}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/owner-details/${ownerId}`)} sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('back') || 'Back'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 1.5, mb: 2.5 }}>
        <Metric label={t('total_horses') || 'Total horses'} value={stats.total} />
        <Metric label={t('stallions') || 'Stallions'} value={stats.stallions} color={COLORS.blue} soft={COLORS.blueSoft} />
        <Metric label={t('mares') || 'Mares'} value={stats.mares} color={COLORS.rose} soft={COLORS.roseSoft} />
        <Metric label={t('housed') || 'Housed'} value={stats.housed} color={COLORS.green} soft={COLORS.greenSoft} />
      </Box>

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1.25} sx={{ p: 2 }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('search_by_horse_or_rfid') || 'Search by horse or RFID'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', md: 420 } }}
          />
          <Chip label={`${filtered.length} ${t('records') || 'records'}`} sx={{ bgcolor: COLORS.goldSoft, fontWeight: 900, alignSelf: { xs: 'flex-start', md: 'center' } }} />
        </Stack>

        <TableContainer>
          <Table size="small" sx={{ ...smartTableSx, tableLayout: 'fixed', minWidth: 980 }}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" width={56}>#</TableCell>
                <TableCell width={260}>{t('horse_name') || 'Horse'}</TableCell>
                <TableCell align="center" width={160}>RFID</TableCell>
                <TableCell align="center" width={130}>{t('gender') || 'Gender'}</TableCell>
                <TableCell align="center" width={160}>{t('breed') || 'Breed'}</TableCell>
                <TableCell align="center" width={130}>{t('color') || 'Color'}</TableCell>
                <TableCell align="center" width={160}>{t('location') || 'Location'}</TableCell>
                <TableCell align="center" width={130}>{t('actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress size={26} /></TableCell>
                </TableRow>
              ) : filtered.length ? filtered.map((horse, index) => {
                const gender = genderMeta(horse.gender, t);
                const displayName = isArabic ? horse.name_arabic || horse.name : horse.name || horse.name_arabic;
                return (
                  <TableRow key={horse.horse_id} hover>
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Avatar src={horse.image_url} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.gold, width: 42, height: 42 }}>
                          {(displayName || '-').charAt(0)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography title={displayName || '-'} sx={{ color: COLORS.text, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {displayName || '-'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: COLORS.muted }}>
                            {isArabic ? horse.name || '-' : horse.name_arabic || '-'}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="center"><Chip size="small" label={horse.rfid_chip_number || '-'} sx={{ bgcolor: COLORS.cardSoft, fontWeight: 800 }} /></TableCell>
                    <TableCell align="center"><Chip size="small" icon={gender.icon} label={gender.label} sx={{ bgcolor: gender.bg, color: gender.color, fontWeight: 900 }} /></TableCell>
                    <TableCell align="center">{isArabic ? horse.breed_arabic || horse.breed_english || '-' : horse.breed_english || horse.breed_arabic || '-'}</TableCell>
                    <TableCell align="center">{isArabic ? horse.color_arabic || horse.color_english || '-' : horse.color_english || horse.color_arabic || '-'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        icon={<HomeIcon />}
                        label={horse.stable_name && horse.room_name ? `${horse.stable_name} - ${horse.room_name}` : t('room_name_not_available') || '-'}
                        sx={{ bgcolor: horse.stable_name ? COLORS.greenSoft : COLORS.warningSoft, fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('view_details') || 'View details'}>
                        <Button size="small" startIcon={<OpenInNewIcon />} onClick={() => navigate(`/horse-details-client/${horse.horse_id}`)} sx={{ color: COLORS.gold, fontWeight: 900 }}>
                          {t('view') || 'View'}
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, color: COLORS.muted }}>
                    {t('no_data') || 'No data'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function Metric({ label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
      <Stack direction="row" spacing={1.4} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color }}><HorseIcon size={20} /></Avatar>
        <Box>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
